/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import {
  format,
  parse,
  startOfWeek,
  getDay,
  startOfDay,
  endOfDay,
  isBefore,
  startOfMonth,
  endOfMonth,
  endOfWeek,
} from 'date-fns';
import { vi } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import {
  Alert,
  Badge,
  Button,
  Col,
  Container,
  Form,
  ListGroup,
  Modal,
  Row,
  Accordion,
  Spinner,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { daDenGioDanhVang, coQuyenGoVangHienTai, NGUONG_PHUT_DANH_VANG } from '../utils/quyenVangCabin';
import calcomService from '../services/calcom';
import { DANH_SACH_CABIN, timCabinTheoId, tenCabinTheoId } from '../constants/cabin.constant';
import AdminLogoutButton from '../components/AdminLogoutButton';
import NhapGio24h from '../components/NhapGio24h';

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: { vi: vi },
});

const messages = {
  allDay: 'Cả ngày',
  previous: 'Trước',
  next: 'Tiếp',
  today: 'Hôm nay',
  month: 'Tháng',
  week: 'Tuần',
  day: 'Ngày',
  agenda: 'Lịch trình',
  date: 'Ngày',
  time: 'Thời gian',
  event: 'Sự kiện',
  noEventsInRange: 'Không có sự kiện trong khoảng thời gian này.',
  showMore: (total) => `+ Xem thêm ${total} sự kiện`,
};

const BOOKING_STATUS = {
  pending: 'Đang chờ xác nhận',
  accepted: 'Đã đăng ký',
  cancelled: 'Đã hủy',
  rejected: 'Từ chối',
  absent: 'Vắng mặt',
};

const BOOKING_STATUS_BADGE = {
  accepted: 'success',
  cancelled: 'danger',
  absent: 'warning',
  pending: 'secondary',
  rejected: 'dark',
};

// Dùng chung với CalcomPage. Thêm cabin thì sửa constants/cabin.constant,
// không chép sang đây — đó chính là thứ file này vừa bỏ đi.
const eventOptions = DANH_SACH_CABIN;

function AdminCalcomPage() {
  const [selectedEvent, setSelectedEvent] = useState(eventOptions[0]?.id || '');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [slots, setSlots] = useState([]);
  const [showDayModal, setShowDayModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [cancelingUid, setCancelingUid] = useState('');
  const [markingAbsentUid, setMarkingAbsentUid] = useState('');
  // Modal xem thông tin GetFly của học viên (tải về hiển thị inline).
  const [gfInfo, setGfInfo] = useState(null); // { loading, error, mapper, accountId, name }
  const [showGfInfo, setShowGfInfo] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [busyMode, setBusyMode] = useState('allDay');
  const [busyStartTime, setBusyStartTime] = useState('09:00');
  const [busyEndTime, setBusyEndTime] = useState('12:00');
  const [busySubmitting, setBusySubmitting] = useState(false);
  const [busyRemoving, setBusyRemoving] = useState(false);
  const [availabilityByDate, setAvailabilityByDate] = useState({});

  const getEventConfigById = timCabinTheoId;

  const getBookingStart = (booking) => booking?.start || booking?.data?.start;
  const getBookingEnd = (booking) => booking?.end || booking?.data?.end;
  const getBookingUid = (booking) =>
    booking?.uid || booking?.data?.uid || booking?.bookingUid || booking?.id;
  const hasAbsentMarker = (booking) => {
    const attendees = [
      ...(Array.isArray(booking?.attendees) ? booking.attendees : []),
      ...(Array.isArray(booking?.data?.attendees) ? booking.data.attendees : []),
    ];

    const hasAbsentAttendee = attendees.some((attendee) =>
      Boolean(
        attendee?.absent ||
        attendee?.isAbsent ||
        attendee?.noShow ||
        attendee?.no_show,
      ),
    );

    return Boolean(
      booking?.absent ||
      booking?.data?.absent ||
      booking?.noShowHost ||
      booking?.data?.noShowHost ||
      hasAbsentAttendee,
    );
  };

  const getBookingStatus = (booking) => {
    if (hasAbsentMarker(booking)) return 'absent';
    return booking?.status || booking?.data?.status || 'pending';
  };
  const getBookingEventTypeId = (booking) =>
    booking?.eventTypeId || booking?.data?.eventTypeId || booking?.eventType?.id;
  const getBookingAttendeeName = (booking) =>
    booking?.attendee?.name ||
    booking?.data?.attendee?.name ||
    booking?.attendees?.[0]?.name ||
    booking?.data?.attendees?.[0]?.name ||
    booking?.bookingFieldsResponses?.name ||
    'Không rõ';
  const getBookingAttendeeEmails = (booking) => {
    const candidates = [
      booking?.attendee?.email,
      booking?.data?.attendee?.email,
      booking?.bookingFieldsResponses?.email,
      ...(Array.isArray(booking?.attendees) ? booking.attendees.map((item) => item?.email) : []),
      ...(Array.isArray(booking?.data?.attendees) ? booking.data.attendees.map((item) => item?.email) : []),
    ];
    return [...new Set(candidates.filter(Boolean))];
  };
  const getBookingTitle = (booking) => booking?.title || booking?.data?.title || '';
  const getBookingLocation = (booking) => booking?.location || booking?.data?.location || '';
  // GetFly CRM student-detail deep link. Teacher-created bookings stamp
  // the CRM account id (makh) into metadata.account_id; student-mode
  // (LMS) bookings don't carry it → no button for those.
  const GETFLY_ACCOUNT_URL = 'https://quangminhphat.getflycrm.com/#/crm/view_account/';
  const getBookingCrmAccountId = (booking) => {
    const metadata = booking?.metadata || booking?.data?.metadata || {};
    const id = metadata?.account_id ?? metadata?.makh;
    if (id === null || id === undefined) return null;
    const trimmed = String(id).trim();
    return trimmed ? trimmed : null;
  };

  // Mã học viên (registrationCode) — MỌI booking đều có ma_hoc_vien trong
  // metadata (cả student-mode lẫn teacher), dùng để tra GetFly theo field 119.
  const getBookingRegistrationCode = (booking) => {
    const metadata = booking?.metadata || booking?.data?.metadata || {};
    const code = metadata?.ma_hoc_vien ?? metadata?.maHocVien;
    const trimmed = code == null ? '' : String(code).trim();
    return trimmed || null;
  };

  // Tải thông tin cơ bản học viên trên GetFly rồi mở modal (không mở link).
  const handleViewGetflyInfo = async (booking) => {
    const registrationCode = getBookingRegistrationCode(booking);
    const name = getBookingAttendeeName(booking);
    if (!registrationCode) {
      toast.warn('Booking này không có mã học viên để tra GetFly.');
      return;
    }
    setShowGfInfo(true);
    setGfInfo({ loading: true, error: null, mapper: null, accountId: null, name });
    try {
      const res = await calcomService.getStudentGetflyInfo(registrationCode);
      const data = res?.data || {};
      setGfInfo({ loading: false, error: null, mapper: data.mapper || null, accountId: data.accountId || null, name });
    } catch (err) {
      setGfInfo({
        loading: false,
        error: err?.response?.data?.message || 'Không tải được thông tin GetFly',
        mapper: null, accountId: null, name,
      });
    }
  };

  const getBookingCourse = (booking) => {
    const metadata = booking?.metadata || booking?.data?.metadata || {};
    const courseCandidates = [
      metadata?.khoa_hoc,
      metadata?.khoaHoc,
      metadata?.course,
      metadata?.courseName,
    ];

    const matchedCourse = courseCandidates.find((item) => {
      if (!item) return false;
      if (typeof item === 'string') return Boolean(item.trim());
      if (typeof item === 'object') {
        return Boolean(item?.value || item?.display_value || item?.name || item?.label);
      }
      return false;
    });

    if (!matchedCourse) return '';
    if (typeof matchedCourse === 'string') return matchedCourse.trim();

    return (
      matchedCourse?.display_value ||
      matchedCourse?.value ||
      matchedCourse?.name ||
      matchedCourse?.label ||
      ''
    );
  };
  const getEventTypeName = (booking) => {
    const eventId = getBookingEventTypeId(booking);
    if (!eventId) return 'Không xác định';
    return tenCabinTheoId(eventId);
  };
  const canCancelBooking = (booking) => {
    const status = getBookingStatus(booking);
    const bookingStart = getBookingStart(booking);
    const isPastBooking = bookingStart ? isBefore(new Date(bookingStart), new Date()) : false;
    if (['cancelled', 'rejected', 'absent'].includes(status)) return false;
    if (isPastBooking) return false;
    return Boolean(getBookingUid(booking));
  };
  // Đánh vắng được sau khi ca chạy 30 phút, KHÔNG phải chờ hết ca.
  //
  // Luật cũ bắt `now >= end`. Đo trên 3.087 ca: ca dài 130 phút (81,2%) hoặc
  // 120 phút (18,8%) — người trực phải nhớ quay lại sau hơn hai tiếng để đánh
  // vắng một học viên đã bỏ về từ lâu, và chỉ 3,4% số ca được đánh vắng.
  //
  // Backend gác y hệt; ẩn nút chỉ để khỏi bấm nhầm.
  const canMarkAbsentBooking = (booking) => {
    const status = getBookingStatus(booking);
    if (status !== 'accepted') return false;
    if (!daDenGioDanhVang(getBookingStart(booking))) return false;
    return Boolean(getBookingUid(booking));
  };

  // Gỡ đánh dấu vắng: chỉ admin, và chỉ với ca đang bị đánh vắng. Đánh ở phút
  // 30 thì học viên tới ở phút 45 là chuyện có thật — không có đường gỡ thì
  // cờ vắng sai nằm lại vĩnh viễn, mà đây chính là sổ điểm danh của hệ thống.
  const canUnmarkAbsentBooking = (booking) => {
    if (!isBookingAbsent(booking)) return false;
    if (!coQuyenGoVangHienTai()) return false;
    return Boolean(getBookingUid(booking));
  };

  /** Ca đang bị đánh vắng chưa — Cal.com trả cờ ở `attendees[].absent`/`noShow`. */
  const isBookingAbsent = (booking) => {
    if (getBookingStatus(booking) === 'absent') return true;
    const ds = booking?.attendees || booking?.data?.attendees || [];
    return Array.isArray(ds) && ds.length > 0
      && ds.some((a) => a?.absent === true || a?.noShow === true || a?.noShowHost === true);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const calendarEvents = useMemo(() => {
    return bookings
      .filter((booking) => {
        if (selectedEvent && selectedEvent !== 'all') {
          const eventId = getBookingEventTypeId(booking);
          return eventId?.toString() === selectedEvent;
        }
        return true;
      })
      .map((booking) => {
        const start = new Date(getBookingStart(booking));
        const end = new Date(getBookingEnd(booking));
        return {
          title: `${getBookingAttendeeName(booking)} - ${BOOKING_STATUS[getBookingStatus(booking)] || 'Không rõ'}`,
          start,
          end,
          resource: booking,
        };
      });
  }, [bookings, selectedEvent]);

  const fetchBookings = useCallback(async (eventId = selectedEvent, date = currentDate, currentView = view) => {
    setLoading(true);
    try {
      const selectedEventConfig = getEventConfigById(eventId);
      let startTime, endTime;
      if (currentView === 'day') {
        startTime = startOfDay(date).toISOString();
        endTime = endOfDay(date).toISOString();
      } else if (currentView === 'week') {
        startTime = startOfWeek(date, { weekStartsOn: 1 }).toISOString();
        endTime = endOfWeek(date, { weekStartsOn: 1 }).toISOString();
      } else {
        const startOfMonthDate = startOfMonth(date);
        const endOfMonthDate = endOfMonth(date);
        startTime = startOfWeek(startOfMonthDate, { weekStartsOn: 1 }).toISOString();
        endTime = endOfWeek(endOfMonthDate, { weekStartsOn: 1 }).toISOString();
      }
      const response = await calcomService.getAllBookings({
        serviceKey: selectedEventConfig?.serviceKey,
        eventTypeId: eventId,
        start: startTime,
        end: endTime,
      });
      const data = response?.data?.data || response?.data || [];
      const rows = Array.isArray(data) ? data : data?.bookings || data?.data || [];
      setBookings(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Không thể tải danh sách booking');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [currentDate, selectedEvent, view]);

  const fetchSlots = useCallback(async (eventId, date) => {
    setLoading(true);
    try {
      const selectedEventConfig = getEventConfigById(eventId);
      const startOfMonthDate = startOfMonth(date);
      const endOfMonthDate = endOfMonth(date);
      const startTime = startOfWeek(startOfMonthDate, { weekStartsOn: 1 }).toISOString();
      const endTime = endOfWeek(endOfMonthDate, { weekStartsOn: 1 }).toISOString();
      const response = await calcomService.getAvailableSlots(
        eventId,
        startTime,
        endTime,
        { serviceKey: selectedEventConfig?.serviceKey },
      );
      const slotsData = response?.data?.data || {};
      const slotList = [];
      Object.keys(slotsData).forEach((dateKey) => {
        slotsData[dateKey].forEach((slot) => {
          slotList.push({
            ...slot,
            start: new Date(slot.start),
            end: new Date(slot.end),
          });
        });
      });
      setSlots(slotList);
    } catch (error) {
      console.error('Error fetching slots:', error);
      setSlots([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAvailability = useCallback(async (eventId, date) => {
    if (!eventId) return;
    try {
      const eventConfig = getEventConfigById(eventId);
      const startOfMonthDate = startOfMonth(date);
      const endOfMonthDate = endOfMonth(date);
      const from = format(startOfWeek(startOfMonthDate, { weekStartsOn: 1 }), 'yyyy-MM-dd');
      const to = format(endOfWeek(endOfMonthDate, { weekStartsOn: 1 }), 'yyyy-MM-dd');
      const response = await calcomService.getEventTypeAvailability(eventId, {
        from,
        to,
        serviceKey: eventConfig?.serviceKey,
      });
      const days = response?.data?.days || response?.data?.data?.days || [];
      const map = {};
      days.forEach((day) => {
        if (day?.date) map[day.date] = day;
      });
      setAvailabilityByDate(map);
    } catch (error) {
      console.error('Error fetching availability:', error);
      setAvailabilityByDate({});
    }
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      fetchBookings(selectedEvent, currentDate);
      fetchSlots(selectedEvent, currentDate);
      fetchAvailability(selectedEvent, currentDate);
    } else {
      setBookings([]);
      setSlots([]);
      setAvailabilityByDate({});
    }
  }, [selectedEvent, currentDate, fetchBookings, fetchSlots, fetchAvailability]);

  const handleSelectSlot = (slotInfo) => {
    const selected = slotInfo.start;
    setSelectedDate(selected);
    setShowDayModal(true);
  };

  const openDayModalByDate = (date) => {
    if (!date) return;
    setSelectedDate(date);
    setShowDayModal(true);
  };

  const handleSelectEvent = (calendarEvent) => {
    openDayModalByDate(calendarEvent?.start);
  };

  const closeDayModal = () => {
    setShowDayModal(false);
    setSelectedDate(null);
    setBusyMode('allDay');
    setBusyStartTime('09:00');
    setBusyEndTime('12:00');
  };

  const handleCancelBooking = async (booking) => {
    const bookingStart = getBookingStart(booking);
    if (bookingStart && isBefore(new Date(bookingStart), new Date())) {
      toast.info('Lịch trong quá khứ không thể hủy. Vui lòng dùng Đánh dấu vắng mặt.');
      return;
    }

    const uid = getBookingUid(booking);
    if (!uid) {
      toast.error('Không tìm thấy booking UID');
      return;
    }

    const confirmCancel = window.confirm('Bạn có chắc chắn muốn hủy booking này?');
    if (!confirmCancel) return;

    const cancellationReason = window.prompt('Nhập lý do hủy (bắt buộc):', '') || '';
    if (!cancellationReason.trim()) {
      toast.error('Lý do hủy là bắt buộc.');
      return;
    }

    setCancelingUid(uid);
    try {
      const bookingEventId = getBookingEventTypeId(booking);
      const bookingEventConfig = getEventConfigById(bookingEventId);
      await calcomService.cancelBooking(
        uid,
        cancellationReason.trim(),
        bookingEventConfig?.serviceKey,
      );
      toast.success('Đã hủy booking');
      await fetchBookings(selectedEvent, currentDate);
    } catch (error) {
      console.error('Cancel booking error:', error);
      toast.error('Hủy booking thất bại');
    } finally {
      setCancelingUid('');
    }
  };

  const handleMarkAbsentBooking = async (booking) => {
    const uid = getBookingUid(booking);
    if (!uid) {
      toast.error('Không tìm thấy booking UID');
      return;
    }

    const confirmAbsent = window.confirm('Xác nhận đánh dấu học viên vắng mặt cho booking này?');
    if (!confirmAbsent) return;

    const attendeeEmails = getBookingAttendeeEmails(booking);
    if (attendeeEmails.length === 0) {
      toast.error('Không tìm thấy email học viên để đánh dấu vắng mặt.');
      return;
    }

    setMarkingAbsentUid(uid);
    try {
      const bookingEventId = getBookingEventTypeId(booking);
      const bookingEventConfig = getEventConfigById(bookingEventId);
      await calcomService.markAbsentBooking(uid, {
        serviceKey: bookingEventConfig?.serviceKey,
        host: false,
        attendees: attendeeEmails.map((email) => ({
          email,
          absent: true,
        })),
      });
      toast.success('Đã đánh dấu vắng mặt');
      await fetchBookings(selectedEvent, currentDate);
    } catch (error) {
      console.error('Mark absent booking error:', error);
      toast.error('Đánh dấu vắng mặt thất bại');
    } finally {
      setMarkingAbsentUid('');
    }
  };

  /**
   * Gỡ đánh dấu vắng — gửi đúng endpoint cũ với `absent: false`.
   *
   * Chỉ admin thấy nút này, và backend gác lại lần nữa: giáo viên/reporter gọi
   * thẳng API vẫn bị 403. Xoá dấu vết một buổi vắng phải là việc của người
   * chịu trách nhiệm.
   */
  const handleUnmarkAbsentBooking = async (booking) => {
    const uid = getBookingUid(booking);
    if (!uid) {
      toast.error('Không tìm thấy booking UID');
      return;
    }
    if (!window.confirm('Gỡ đánh dấu vắng mặt cho booking này? Ca sẽ được tính là đã học.')) return;

    const attendeeEmails = getBookingAttendeeEmails(booking);
    if (attendeeEmails.length === 0) {
      toast.error('Không tìm thấy email học viên để gỡ đánh dấu.');
      return;
    }

    setMarkingAbsentUid(uid);
    try {
      const bookingEventConfig = getEventConfigById(getBookingEventTypeId(booking));
      await calcomService.markAbsentBooking(uid, {
        serviceKey: bookingEventConfig?.serviceKey,
        host: false,
        attendees: attendeeEmails.map((email) => ({ email, absent: false })),
      });
      toast.success('Đã gỡ đánh dấu vắng mặt');
      await fetchBookings(selectedEvent, currentDate);
    } catch (error) {
      console.error('Unmark absent booking error:', error);
      toast.error(error?.response?.data?.message || 'Gỡ đánh dấu vắng mặt thất bại');
    } finally {
      setMarkingAbsentUid('');
    }
  };

  const formatDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const refreshAfterBusyChange = async () => {
    await Promise.all([
      fetchSlots(selectedEvent, currentDate),
      fetchAvailability(selectedEvent, currentDate),
    ]);
  };

  const rangeCoversAllAvailability = (start, end, windows) => {
    if (!windows || windows.length === 0) return true;
    return windows.every((w) => start <= w.startTime && w.endTime <= end);
  };

  const handleMarkBusy = async () => {
    if (!selectedDate || !selectedEvent) return;
    const eventConfig = getEventConfigById(selectedEvent);

    if (busyMode === 'range') {
      if (!busyStartTime || !busyEndTime) {
        toast.error('Vui lòng nhập giờ bắt đầu và kết thúc.');
        return;
      }
      if (busyEndTime <= busyStartTime) {
        toast.error('Giờ kết thúc phải sau giờ bắt đầu.');
        return;
      }
    }

    const dateKey = formatDateKey(selectedDate);
    const dayInfo = availabilityByDate[dateKey];
    const baseWindows = dayInfo?.availableWindows?.length
      ? dayInfo.availableWindows
      : dayInfo?.baseWindows || [];

    if (busyMode === 'range' && rangeCoversAllAvailability(busyStartTime, busyEndTime, baseWindows)) {
      const winLabel = baseWindows.length
        ? baseWindows.map((w) => `${w.startTime}–${w.endTime}`).join(', ')
        : 'trống';
      const proceed = window.confirm(
        `Khoảng ${busyStartTime}–${busyEndTime} đang bao phủ hết lịch làm việc của ngày (${winLabel}). ` +
        `Hệ thống sẽ tự ghi nhận là CẢ NGÀY bận. Tiếp tục?`
      );
      if (!proceed) return;
    } else {
      const confirmMsg = busyMode === 'allDay'
        ? `Đánh dấu cả ngày ${selectedDate.toLocaleDateString('vi-VN')} là bận?`
        : `Đánh dấu bận từ ${busyStartTime} đến ${busyEndTime} ngày ${selectedDate.toLocaleDateString('vi-VN')}?`;
      if (!window.confirm(confirmMsg)) return;
    }

    setBusySubmitting(true);
    try {
      const payload = {
        serviceKey: eventConfig?.serviceKey,
        date: dateKey,
        ...(busyMode === 'allDay'
          ? { allDay: true }
          : { startTime: busyStartTime, endTime: busyEndTime }),
      };
      console.log('[markBusy] payload', payload);
      await calcomService.markEventTypeBusy(selectedEvent, payload);
      toast.success('Đã đánh dấu bận');
      await refreshAfterBusyChange();
    } catch (error) {
      console.error('Mark busy error:', error);
      toast.error(error?.response?.data?.message || 'Đánh dấu bận thất bại');
    } finally {
      setBusySubmitting(false);
    }
  };

  const handleRemoveBusy = async () => {
    if (!selectedDate || !selectedEvent) return;
    const eventConfig = getEventConfigById(selectedEvent);

    if (!window.confirm(`Bỏ đánh dấu bận ngày ${selectedDate.toLocaleDateString('vi-VN')}?`)) return;

    setBusyRemoving(true);
    try {
      const dateKey = formatDateKey(selectedDate);
      await calcomService.removeEventTypeBusy(selectedEvent, dateKey, eventConfig?.serviceKey);
      toast.success('Đã bỏ đánh dấu bận');
      await refreshAfterBusyChange();
    } catch (error) {
      console.error('Remove busy error:', error);
      toast.error(error?.response?.data?.message || 'Bỏ đánh dấu bận thất bại');
    } finally {
      setBusyRemoving(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dayBookings = useMemo(() => {
    if (!selectedDate) return [];
    return bookings
      .filter((booking) => {
        const start = new Date(getBookingStart(booking));
        const sameDay = start.toDateString() === selectedDate.toDateString();
        if (!sameDay) return false;
        if (selectedEvent && selectedEvent !== 'all') {
          const eventId = getBookingEventTypeId(booking);
          return eventId?.toString() === selectedEvent;
        }
        return true;
      })
      .filter((booking) => (filterStatus === 'all' ? true : getBookingStatus(booking) === filterStatus))
      .sort((a, b) => new Date(getBookingStart(a)) - new Date(getBookingStart(b)));
  }, [bookings, selectedDate, selectedEvent, filterStatus]);

  const daySlots = useMemo(() => {
    if (!selectedDate) return [];
    if (isBefore(selectedDate, startOfDay(new Date()))) return [];
    return slots
      .filter((slot) => slot.start.toDateString() === selectedDate.toDateString())
      .sort((a, b) => a.start - b.start);
  }, [slots, selectedDate]);

  const isPastSelectedDate = selectedDate
    ? isBefore(selectedDate, startOfDay(new Date()))
    : false;

  return (
    <Container className='mt-4'>
      <div className='d-flex justify-content-end align-items-center gap-2 mb-2'>
        {/* Đường vào màn cảnh báo lịch trùng. Không có nút này thì trang đó chỉ tới
          * được bằng cách gõ URL, tức là không ai dùng. */}
        <Button
          size='sm'
          variant='outline-warning'
          href='/admin/cabin/trung-lich'
        >
          Cảnh báo lịch trùng
        </Button>
        <AdminLogoutButton />
      </div>
      <h1 className='text-center mb-4'>Quản Lý Lịch Cabin điện tử</h1>

      <Row className='mb-3'>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Chọn máy cabin</Form.Label>
            <Form.Select
              value={selectedEvent}
              onChange={(e) => setSelectedEvent(e.target.value)}
            >
              {eventOptions.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Trạng thái</Form.Label>
            <Form.Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value='all'>Tất cả</option>
              <option value='pending'>Đang chờ</option>
              <option value='accepted'>Đã đăng ký</option>
              <option value='cancelled'>Đã hủy</option>
              <option value='rejected'>Từ chối</option>
              <option value='absent'>Vắng mặt</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={2} className='d-flex align-items-end'>
          <Button
            variant='secondary'
            className='w-100'
            onClick={() => fetchBookings(selectedEvent, currentDate)}
            disabled={loading}
          >
            {loading ? 'Đang tải...' : 'Làm mới danh sách'}
          </Button>
        </Col>
      </Row>

      <div style={{ position: 'relative' }} className='mb-4'>
        {loading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
            }}
          >
            <Alert variant='info'>Đang tải dữ liệu...</Alert>
          </div>
        )}

        <Calendar
          localizer={localizer}
          culture='vi'
          messages={messages}
          views={['month', 'week', 'day']}
          drilldownView='day'
          events={calendarEvents}
          startAccessor='start'
          endAccessor='end'
          style={{ height: 600 }}
          selectable={!loading}
          longPressThreshold={1}
          onSelectSlot={handleSelectSlot}
          onSelectEvent={handleSelectEvent}
          onDrillDown={(date) => {
            setCurrentDate(date);
            setView('day');
            fetchBookings(selectedEvent, date, 'day');
          }}
          onShowMore={(moreEvents, date) => {
            setCurrentDate(date);
            setView('day');
            fetchBookings(selectedEvent, date, 'day');
          }}
          date={currentDate}
          view={view}
          onView={(newView) => {
            setView(newView);
            fetchBookings(selectedEvent, currentDate, newView);
          }}
          onNavigate={(date) => setCurrentDate(date)}
          eventPropGetter={(event) => {
            const status = getBookingStatus(event.resource);
            const backgroundColor =
              status === 'accepted'
                ? '#d1e7dd'
                : status === 'pending'
                  ? '#fff3cd'
                  : status === 'cancelled'
                    ? '#f8d7da'
                    : status === 'absent'
                      ? '#ffe5b4'
                    : '#e2e3e5';
            return {
              style: { backgroundColor, borderRadius: 6, border: '1px solid #ced4da' },
            };
          }}
          dayPropGetter={(date) => {
            const today = startOfDay(new Date());
            const isPast = isBefore(date, today);
            const isToday = date.toDateString() === today.toDateString();
            const hasSlots = slots.some((slot) => slot.start.toDateString() === date.toDateString());
            const hasBookings = bookings.some(
              (booking) => new Date(getBookingStart(booking)).toDateString() === date.toDateString(),
            );
            const dayKey = formatDateKey(date);
            const dayAvailability = availabilityByDate[dayKey];
            let backgroundColor;
            if (dayAvailability?.status === 'busy') {
              backgroundColor = '#f8d7da';
            } else if (dayAvailability?.status === 'partial') {
              backgroundColor = '#ffe5b4';
            } else if (isToday) {
              backgroundColor = '#cfe2ff';
            } else if (hasBookings) {
              backgroundColor = '#e7f1ff';
            } else if (hasSlots) {
              backgroundColor = '#e8f5e9';
            } else {
              backgroundColor = 'white';
            }
            return {
              style: {
                backgroundColor,
                border: isToday ? '2px solid #0d6efd' : '1px solid transparent',
                opacity: isPast ? 0.5 : 1,
              },
            };
          }}
        />
      </div>

      <Modal show={showDayModal} onHide={closeDayModal} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>
            Lịch ngày {selectedDate ? selectedDate.toLocaleDateString('vi-VN') : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Row>
            <Col md={isPastSelectedDate ? 12 : 6}>
              <h6 className='mb-2'>Danh sách đăng ký</h6>
              {(() => {
                const cancelledBookings = dayBookings.filter(booking => getBookingStatus(booking) === 'cancelled');
                const activeBookings = dayBookings.filter(booking => getBookingStatus(booking) !== 'cancelled');
                return (
                  <>
                    {activeBookings.length === 0 && cancelledBookings.length === 0 && (
                      <Alert variant='light'>Không có đăng ký nào.</Alert>
                    )}
                    {activeBookings.length > 0 && (
                      <ListGroup>
                        {activeBookings.map((booking) => (
                          <ListGroup.Item key={getBookingUid(booking)}>
                            <div className='d-flex justify-content-between align-items-start'>
                              <div>
                                <div className='fw-bold'>{getBookingAttendeeName(booking)}</div>
                                {getBookingTitle(booking) && (
                                  <div className='text-muted small'>{getBookingTitle(booking)}</div>
                                )}
                                <div className='small'>
                                  <strong>Máy:</strong> {getEventTypeName(booking)}
                                </div>
                                {getBookingCourse(booking) && (
                                  <div className='small'>
                                    <strong>Khoá học:</strong> {getBookingCourse(booking)}
                                  </div>
                                )}
                                <div>
                                  {new Date(getBookingStart(booking)).toLocaleTimeString('vi-VN')} -{' '}
                                  {new Date(getBookingEnd(booking)).toLocaleTimeString('vi-VN')}
                                </div>
                                {getBookingLocation(booking) && (
                                  <div className='small text-muted'>Địa điểm: {getBookingLocation(booking)}</div>
                                )}
                              </div>
                              <div className='text-end'>
                                <Badge
                                  bg={BOOKING_STATUS_BADGE[getBookingStatus(booking)] || 'secondary'}
                                  text={getBookingStatus(booking) === 'absent' ? 'dark' : undefined}
                                  className='mb-2 d-block'
                                >
                                  {BOOKING_STATUS[getBookingStatus(booking)] || 'Không rõ'}
                                </Badge>
                                {getBookingRegistrationCode(booking) && (
                                  <Button
                                    size='sm'
                                    variant='outline-info'
                                    className='mb-2 d-block w-100'
                                    onClick={() => handleViewGetflyInfo(booking)}
                                  >
                                    Xem thông tin
                                  </Button>
                                )}
                                {getBookingCrmAccountId(booking) && (
                                  <Button
                                    size='sm'
                                    variant='outline-primary'
                                    className='mb-2'
                                    href={`${GETFLY_ACCOUNT_URL}${getBookingCrmAccountId(booking)}`}
                                    target='_blank'
                                    rel='noreferrer'
                                  >
                                    Mở GetFly
                                  </Button>
                                )}
                                {canMarkAbsentBooking(booking) && (
                                  <Button
                                    size='sm'
                                    variant='outline-warning'
                                    className='mb-2'
                                    disabled={markingAbsentUid === getBookingUid(booking)}
                                    onClick={() => handleMarkAbsentBooking(booking)}
                                  >
                                    {markingAbsentUid === getBookingUid(booking)
                                      ? 'Đang cập nhật...'
                                      : 'Đánh dấu vắng mặt'}
                                  </Button>
                                )}
                                {canUnmarkAbsentBooking(booking) && (
                                  <Button
                                    size='sm'
                                    variant='outline-secondary'
                                    className='mb-2'
                                    disabled={markingAbsentUid === getBookingUid(booking)}
                                    onClick={() => handleUnmarkAbsentBooking(booking)}
                                    title={`Chỉ admin gỡ được. Đánh vắng từ phút ${NGUONG_PHUT_DANH_VANG}.`}
                                  >
                                    {markingAbsentUid === getBookingUid(booking)
                                      ? 'Đang cập nhật...'
                                      : 'Gỡ đánh dấu vắng'}
                                  </Button>
                                )}
                                {canCancelBooking(booking) && (
                                  <Button
                                    size='sm'
                                    variant='outline-danger'
                                    disabled={cancelingUid === getBookingUid(booking)}
                                    onClick={() => handleCancelBooking(booking)}
                                  >
                                    {cancelingUid === getBookingUid(booking) ? 'Đang hủy...' : 'Hủy lịch'}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    )}
                    {cancelledBookings.length > 0 && (
                      <Accordion defaultActiveKey="" className="mt-3">
                        <Accordion.Item eventKey="cancelled">
                          <Accordion.Header>Đã hủy ({cancelledBookings.length})</Accordion.Header>
                          <Accordion.Body>
                            <ListGroup>
                              {cancelledBookings.map((booking) => (
                                <ListGroup.Item key={getBookingUid(booking)}>
                                  <div className='d-flex justify-content-between align-items-start'>
                                    <div>
                                      <div className='fw-bold'>{getBookingAttendeeName(booking)}</div>
                                      {getBookingTitle(booking) && (
                                        <div className='text-muted small'>{getBookingTitle(booking)}</div>
                                      )}
                                      <div className='small'>
                                        <strong>Máy:</strong> {getEventTypeName(booking)}
                                      </div>
                                      {getBookingCourse(booking) && (
                                        <div className='small'>
                                          <strong>Khoá học:</strong> {getBookingCourse(booking)}
                                        </div>
                                      )}
                                      <div>
                                        {new Date(getBookingStart(booking)).toLocaleTimeString('vi-VN')} -{' '}
                                        {new Date(getBookingEnd(booking)).toLocaleTimeString('vi-VN')}
                                      </div>
                                      {getBookingLocation(booking) && (
                                        <div className='small text-muted'>Địa điểm: {getBookingLocation(booking)}</div>
                                      )}
                                    </div>
                                    <div className='text-end'>
                                      <Badge
                                        bg={BOOKING_STATUS_BADGE[getBookingStatus(booking)] || 'secondary'}
                                        text={getBookingStatus(booking) === 'absent' ? 'dark' : undefined}
                                        className='mb-2 d-block'
                                      >
                                        {BOOKING_STATUS[getBookingStatus(booking)] || 'Không rõ'}
                                      </Badge>
                                      {getBookingCrmAccountId(booking) && (
                                        <Button
                                          size='sm'
                                          variant='outline-primary'
                                          href={`${GETFLY_ACCOUNT_URL}${getBookingCrmAccountId(booking)}`}
                                          target='_blank'
                                          rel='noreferrer'
                                        >
                                          Mở GetFly
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                </ListGroup.Item>
                              ))}
                            </ListGroup>
                          </Accordion.Body>
                        </Accordion.Item>
                      </Accordion>
                    )}
                  </>
                );
              })()}
            </Col>
            {!isPastSelectedDate && (
              <Col md={6}>
                <h6 className='mb-2'>Lịch trống</h6>
                {daySlots.length === 0 && (
                  <Alert variant='light'>Không có lịch trống.</Alert>
                )}
                <ListGroup>
                  {daySlots.map((slot, index) => (
                    <ListGroup.Item key={`${slot.start.toISOString()}-${index}`}>
                      {slot.start.toLocaleTimeString('vi-VN')} - {slot.end.toLocaleTimeString('vi-VN')}
                    </ListGroup.Item>
                  ))}
                </ListGroup>

                <h6 className='mt-4 mb-2'>Đánh dấu bận</h6>
                {(() => {
                  const dayKey = selectedDate ? formatDateKey(selectedDate) : null;
                  const dayInfo = dayKey ? availabilityByDate[dayKey] : null;
                  const status = dayInfo?.status;
                  const busyRanges = dayInfo?.busyRanges || [];
                  const hasOverrides = (dayInfo?.overrides || []).length > 0;
                  const statusVariant = status === 'busy' ? 'danger' : status === 'partial' ? 'warning' : 'success';
                  const statusLabel = status === 'busy'
                    ? 'Cả ngày bận'
                    : status === 'partial'
                      ? 'Bận một phần'
                      : 'Trống cả ngày';
                  const isFullyBusy = status === 'busy';
                  return (
                    <>
                      {dayInfo && (
                        <div className='mb-2 small'>
                          <div>
                            <Badge bg={statusVariant} className='me-2'>{statusLabel}</Badge>
                            {busyRanges.length > 0 && (
                              <span className='text-muted'>
                                Bận: {busyRanges.map((r) => `${r.startTime}–${r.endTime}`).join(', ')}
                              </span>
                            )}
                          </div>
                          {(() => {
                            const win = dayInfo?.availableWindows?.length
                              ? dayInfo.availableWindows
                              : dayInfo?.baseWindows || [];
                            return (
                              <div className='text-muted mt-1'>
                                Lịch làm việc: {win.length
                                  ? win.map((w) => `${w.startTime}–${w.endTime}`).join(', ')
                                  : '(trống — đánh dấu giờ sẽ thành cả ngày)'}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                      {!isFullyBusy && (
                        <>
                          <Form.Group className='mb-2'>
                            <Form.Check
                              inline
                              type='radio'
                              id='busy-mode-allDay'
                              name='busyMode'
                              label='Cả ngày'
                              checked={busyMode === 'allDay'}
                              onChange={() => setBusyMode('allDay')}
                            />
                            <Form.Check
                              inline
                              type='radio'
                              id='busy-mode-range'
                              name='busyMode'
                              label='Khoảng giờ'
                              checked={busyMode === 'range'}
                              onChange={() => setBusyMode('range')}
                            />
                          </Form.Group>
                          {busyMode === 'range' && (
                            <Row className='g-2 mb-2'>
                              <Col xs={6}>
                                <Form.Label className='small mb-1'>Từ</Form.Label>
                                <NhapGio24h
                                  nhan='Từ'
                                  value={busyStartTime}
                                  onChange={setBusyStartTime}
                                />
                              </Col>
                              <Col xs={6}>
                                <Form.Label className='small mb-1'>Đến</Form.Label>
                                <NhapGio24h
                                  nhan='Đến'
                                  value={busyEndTime}
                                  onChange={setBusyEndTime}
                                />
                              </Col>
                            </Row>
                          )}
                        </>
                      )}
                      <div className='d-flex gap-2'>
                        {!isFullyBusy && (
                          <Button
                            variant='warning'
                            size='sm'
                            onClick={handleMarkBusy}
                            disabled={busySubmitting || busyRemoving}
                          >
                            {busySubmitting ? 'Đang lưu...' : 'Đánh dấu bận'}
                          </Button>
                        )}
                        <Button
                          variant='outline-secondary'
                          size='sm'
                          onClick={handleRemoveBusy}
                          disabled={busySubmitting || busyRemoving || !hasOverrides}
                        >
                          {busyRemoving ? 'Đang bỏ...' : 'Bỏ đánh dấu bận'}
                        </Button>
                      </div>
                    </>
                  );
                })()}
              </Col>
            )}
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={closeDayModal}>
            Đóng
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Thông tin cơ bản học viên trên GetFly (tải inline, không mở link) */}
      <Modal show={showGfInfo} onHide={() => setShowGfInfo(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Thông tin học viên{gfInfo?.name ? ` — ${gfInfo.name}` : ''} (GetFly)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {gfInfo?.loading && (
            <div className='text-center py-3'>
              <Spinner animation='border' size='sm' /> Đang tải dữ liệu từ GetFly...
            </div>
          )}
          {!gfInfo?.loading && gfInfo?.error && (
            <Alert variant='warning' className='mb-0'>{gfInfo.error}</Alert>
          )}
          {!gfInfo?.loading && !gfInfo?.error && gfInfo?.mapper && (
            <>
              <p className='mb-2'>Tên học viên:{' '}
                <strong>{gfInfo.mapper?.account_name?.display_value || 'Không có dữ liệu'}</strong></p>
              <p className='mb-2'>Tên khoá học:{' '}
                <strong>{gfInfo.mapper?.khoa_hoc?.display_value || 'Không có dữ liệu'}</strong></p>
              <p className='mb-2'>Mã học viên:{' '}
                <strong>{gfInfo.mapper?.ma_hoc_vien_theo_csdl?.display_value || 'Không có dữ liệu'}</strong></p>
              <p className='mb-2'>Ngày sinh:{' '}
                <strong>{gfInfo.mapper?.birthday?.display_value || 'Không có dữ liệu'}</strong></p>
              <p className='mb-0'>Số CMND/CCCD/HC:{' '}
                <strong>{gfInfo.mapper?.can_cuoc_cong_dan?.display_value || 'Không có dữ liệu'}</strong></p>
            </>
          )}
          {!gfInfo?.loading && !gfInfo?.error && !gfInfo?.mapper && (
            <p className='text-muted mb-0'>Không có dữ liệu.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          {gfInfo?.accountId && (
            <Button
              variant='outline-primary'
              size='sm'
              href={`${GETFLY_ACCOUNT_URL}${gfInfo.accountId}`}
              target='_blank'
              rel='noreferrer'
            >
              Mở trên GetFly
            </Button>
          )}
          <Button variant='secondary' onClick={() => setShowGfInfo(false)}>Đóng</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default AdminCalcomPage;
